/**
 * SOLID Review
 * S — Strong. The failure shape every client port carries, and the one combinator that wraps a value
 *     schema into the Result envelope.
 * O — Strong. A new port's result is `ResultOf(itsSchema)`; nothing here changes.
 * L — N/A.
 * I — Strong. One schema, one function.
 * D — Good. zod only.
 */
import { z } from 'zod';

/**
 * What a failed port call carries: `{ code, message }`, the same shape the kernel guard emits for
 * `CONTRACT`/`UNAVAILABLE`/`INTERNAL`. Not `shared/Result`'s `Error` object — an `Error` does not
 * survive `JSON.stringify` (its `message` is not an own enumerable property), so a failure that
 * crossed a `postMessage` would arrive empty (frontend strict-ports plan, FE1).
 */
const ErrorPortSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
}).meta({ id: 'ErrorPort' });

/** `Result<T>` as a schema: `{ success: true, value }` or `{ success: false, error }`. */
export function ResultOf<T extends z.ZodTypeAny>(value: T) {
  return z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), value }),
    z.object({ success: z.literal(false), error: ErrorPortSchema }),
  ]);
}

/**
 * `Result<void>` as a schema: the same union, with `value` OPTIONAL — what a void answer IS once it has been
 * carried (frontend strict-ports plan, FE8, 2026-10-04).
 *
 * In process a void success is `{ success: true, value: undefined }`. Across a `postMessage` it is
 * `{"success":true}`: `JSON.stringify` drops a property whose value is `undefined`, and Zod 4 treats a key as
 * optional only when its SCHEMA is, so `ResultOf(z.void())` refused every void answer that crossed a thread as
 * `CONTRACT` (`expected nonoptional, received undefined`). Nothing in process could see it — both ends held
 * the same object. FE8's second worker proof found it: the first void op across the channel
 * (`ClientServerWatch.watch`) failed, and the round went on with no peek. A value-carrying result keeps
 * `value` REQUIRED (`ResultOf`): a missing DTO is a defect the guard must refuse.
 */
export const ResultVoid = z.discriminatedUnion('success', [
  z.object({ success: z.literal(true), value: z.void().optional() }),
  z.object({ success: z.literal(false), error: ErrorPortSchema }),
]);
